import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFlag } from '../../lib/flags';
import { searchRoutes } from '../../lib/routeRegistry';
import { trackSearch } from '../../lib/analytics';
import './SiteSearch.css';

// Blog data is heavy (it imports every blog image as an asset). Load it on
// demand the first time the search opens, so the main bundle stays lean.
let blogIndexPromise = null;
const loadBlogIndex = () => {
  if (!blogIndexPromise) {
    blogIndexPromise = Promise.all([
      import('../../lib/content'),
      import('../../pages/Blog/blogData'),
    ]).then(([content, blog]) => ({
      fullTextSearch: content.fullTextSearch,
      blogs: blog.default || [],
    }));
  }
  return blogIndexPromise;
};

// Cmd+K / Ctrl+K opens a search modal that fuzzy-matches over:
//   - Known site routes (src/lib/routeRegistry.js)
//   - Blog post titles + descriptions (via content layer)
//
// Today: pure client-side. When content moves to a CMS, the content.js
// async helpers + an Algolia/Meilisearch endpoint become drop-in
// replacements with the same interface.

const SiteSearch = () => {
  const enabled = useFlag('siteSearchCmdK');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const [blogIndex, setBlogIndex] = useState(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Combined results: routes (always indexed) + blogs (loaded lazily on
  // first open). When blogIndex is null, blog results just stay empty
  // until the dynamic import resolves.
  const results = useMemo(() => {
    if (!query.trim()) return [];
    const routes = searchRoutes(query, 6).map((r) => ({
      kind: 'route',
      path: r.path,
      title: r.title,
      hint: r.path,
    }));
    const blogs = blogIndex
      ? blogIndex.fullTextSearch(blogIndex.blogs, query).slice(0, 4).map((b) => ({
          kind: 'blog',
          path: `/blog/${b.slug}`,
          title: b.title,
          hint: b.categoryLabel || b.author || 'Blog post',
        }))
      : [];
    return [...routes, ...blogs];
  }, [query, blogIndex]);

  // Global Cmd+K / Ctrl+K + Escape handler.
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    const onKey = (e) => {
      const isOpenShortcut = (e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey);
      if (isOpenShortcut) {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, open]);

  // Focus input + reset state on open. Trigger the blog-index dynamic
  // import the first time the search opens; ignore the result if the
  // user closes the modal before it resolves.
  useEffect(() => {
    if (!open) return;
    setActiveIdx(0);
    setQuery('');
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    if (!blogIndex) {
      let cancelled = false;
      loadBlogIndex().then((idx) => { if (!cancelled) setBlogIndex(idx); });
      return () => { cancelled = true; cancelAnimationFrame(id); };
    }
    return () => cancelAnimationFrame(id);
  }, [open, blogIndex]);

  const dismiss = useCallback(() => setOpen(false), []);

  const choose = useCallback((item) => {
    if (!item) return;
    trackSearch(query, results.length);
    setOpen(false);
    navigate(item.path);
  }, [navigate, query, results.length]);

  const onListKey = (e) => {
    if (!results.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(results[activeIdx]);
    }
  };

  if (!enabled || !open) return null;

  return (
    <div
      className="tn-search"
      role="dialog"
      aria-modal="true"
      aria-label="Search the site"
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <div className="tn-search__card" onKeyDown={onListKey}>
        <div className="tn-search__inputrow">
          <span className="tn-search__icon" aria-hidden="true">⌘K</span>
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
            placeholder="Search pages, articles…"
            className="tn-search__input"
            autoComplete="off"
            spellCheck="false"
          />
          <button type="button" className="tn-search__close" onClick={dismiss} aria-label="Close">×</button>
        </div>

        <ul className="tn-search__results" role="listbox" aria-label="Search results">
          {results.map((r, idx) => (
            <li
              key={`${r.kind}:${r.path}`}
              role="option"
              aria-selected={idx === activeIdx}
              className={`tn-search__item ${idx === activeIdx ? 'is-active' : ''}`}
              onMouseEnter={() => setActiveIdx(idx)}
              onClick={() => choose(r)}
            >
              <span className={`tn-search__kind tn-search__kind--${r.kind}`}>
                {r.kind === 'blog' ? 'Article' : 'Page'}
              </span>
              <span className="tn-search__title">{r.title}</span>
              <span className="tn-search__hint">{r.hint}</span>
            </li>
          ))}
          {query.trim() && !results.length && (
            <li className="tn-search__empty">No matches for "{query}".</li>
          )}
        </ul>

        <footer className="tn-search__foot">
          <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
          <span><kbd>↵</kbd> open</span>
          <span><kbd>esc</kbd> close</span>
        </footer>
      </div>
    </div>
  );
};

export default SiteSearch;
